// ============================================================
// SpringBootTesting.jsx
//
// A branded, responsive course content page for the
// "Testing in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Comprehensive testing suite covering Unit,
//           Integration, Slice, and Security testing.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ScienceIcon            from "@mui/icons-material/Science";
import BugReportIcon          from "@mui/icons-material/BugReport";
import SpeedIcon              from "@mui/icons-material/Speed";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import SecurityIcon           from "@mui/icons-material/Security";
import StorageIcon            from "@mui/icons-material/Storage";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CodeIcon from "@mui/icons-material/Code";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootTesting() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<ScienceIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 20"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Spring Boot <span>Testing Suite</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Quality is built-in, not bolted on. Master the art of testing by covering unit isolation, slice-based integration, and full-stack end-to-end flows using JUnit 5, Mockito, and Spring Test.
                    </Typography>
                </Box>

                {/* 1. UNIT TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="1. Service Isolation (Unit Tests)" />
                    <CodeBlock
                        title="JUnit 5 + Mockito"
                        code={`@ExtendWith(MockitoExtension.class)\nclass UserServiceTest {\n  @Mock private UserRepository repo;\n  @InjectMocks private UserService service;\n\n  @Test\n  void testGetById() {\n    when(repo.findById(1L)).thenReturn(Optional.of(new User("Alice")));\n    assertEquals("Alice", service.getById(1L).getName());\n  }\n}`}
                    />
                </Box>

                {/* 2. CONTROLLER TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="2. Web Slice (MockMvc)" />
                    <CodeBlock
                        title="@WebMvcTest"
                        code={`@WebMvcTest(UserController.class)\nclass UserControllerTest {\n  @Autowired private MockMvc mockMvc;\n  @MockBean private UserService service;\n\n  @Test\n  void testApi() throws Exception {\n    mockMvc.perform(get("/api/users"))\n           .andExpect(status().isOk());\n  }\n}`}
                    />
                </Box>

                {/* 3. JPA TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="3. Data Slice (H2/Database)" />
                    <CodeBlock
                        title="@DataJpaTest"
                        code={`@DataJpaTest\nclass UserRepoTest {\n  @Autowired private UserRepository repo;\n\n  @Test\n  void testSave() {\n    User saved = repo.save(new User("Bob"));\n    assertThat(saved.getId()).isNotNull();\n  }\n}`}
                    />
                </Box>

                {/* 4. INTEGRATION TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="4. Full Context (@SpringBootTest)" />
                    <CodeBlock
                        title="Random Port Test"
                        code={`@SpringBootTest(webEnvironment = RANDOM_PORT)\nclass AppE2ETest {\n  @Autowired private TestRestTemplate rest;\n\n  @Test\n  void healthCheck() {\n    var res = rest.getForEntity("/api/status", String.class);\n    assertThat(res.getStatusCode()).isEqualTo(OK);\n  }\n}`}
                    />
                </Box>

                {/* 5. JSON TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="5. Payload Verification (@JsonTest)" />
                    <CodeBlock
                        title="JacksonTester"
                        code={`@JsonTest\nclass UserJsonTest {\n  @Autowired private JacksonTester<UserDto> json;\n\n  @Test\n  void serialize() throws Exception {\n    UserDto dto = new UserDto("Alice", "alice@pro.dev");\n    assertThat(json.write(dto)).hasJsonPathStringValue("@.name", "Alice");\n  }\n}`}
                    />
                </Box>

                {/* 6. SECURITY TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="6. Auth & RBAC Testing" />
                    <CodeBlock
                        title="@WithMockUser"
                        code={`@WebMvcTest(AdminController.class)\nclass AdminTest {\n  @Autowired private MockMvc mvc;\n\n  @Test\n  @WithMockUser(roles = "ADMIN")\n  void adminAccess() throws Exception {\n    mvc.perform(get("/admin")).andExpect(status().isOk());\n  }\n}`}
                    />
                </Box>

                {/* 7. ASYNC TESTING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="7. Testing @Async Methods" />
                    <CodeBlock
                        title="Awaitility"
                        code={`@Test\nvoid asyncTaskTest() {\n  service.doTask();\n  // Wait up to 5s for the state to change\n  await().atMost(5, SECONDS).until(() -> repo.count() > 0);\n}`}
                    />
                </Box>

                {/* 8. PARAMETERIZED TESTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="8. Parameterized Unit Tests" />
                    <CodeBlock
                        title="@ParameterizedTest"
                        code={`@ParameterizedTest\n@ValueSource(strings = {"admin", "user", "guest"})\nvoid roleValidation(String role) {\n  assertTrue(validator.isValid(role));\n}`}
                    />
                </Box>

                {/* 9. MOCKING STATIC METHODS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LightbulbIcon />} label="9. Mocking Static Logic" />
                    <CodeBlock
                        title="mockStatic"
                        code={`try (MockedStatic<Utils> utils = mockStatic(Utils.class)) {\n  utils.when(Utils::now).thenReturn(fixedTime);\n  // run test involving static call\n}`}
                    />
                </Box>

                {/* 10. ERROR HANDLING TESTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ErrorOutlineIcon sx={{ color: BRAND.gold }} />} label="10. Global Exception Testing" />
                    <CodeBlock
                        title="Exception Interception"
                        code={`@Test\nvoid testNotFound() throws Exception {\n  when(service.find(99L)).thenThrow(new ResourceNotFoundException());\n  mockMvc.perform(get("/api/items/99"))\n         .andExpect(status().isNotFound())\n         .andExpect(jsonPath("$.error").value("Not Found"));\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* SUMMARY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="The Testing Philosophy" />
                    <Grid container spacing={1.5}>
                        {[
                            "Mock external dependencies to keep unit tests fast and deterministic.",
                            "Use @DataJpaTest with in-memory H2 for rapid persistence validation.",
                            "Test failure paths (400, 404, 500) as rigorously as success paths.",
                            "Leverage MockMvc to verify REST contracts without starting a server.",
                            "Utilize @WithMockUser to validate RBAC and Security Filter Chains.",
                            "Keep end-to-end (@SpringBootTest) suites lean; focus on high-risk flows."
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Tests are your safety net. Aim for high coverage in the <strong>Service Layer</strong> (Unit Tests) and <strong>Repository Layer</strong> (Slice Tests) to ensure your business rules and data constraints are airtight.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
